import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { AlertTriangle, DollarSign, Clock, FileText, Send, Loader2, Download } from 'lucide-react';
import { apiInvoices, apiCustomers, email, settingsApi } from '../../lib/api';
import { fmt } from '../../lib/accountingStore';
import { printHTML } from '../../lib/importExport';
import { collectionLetterHTML } from '../../lib/companyPolicies';
import { useConfig } from '../../context/ConfigContext';
import { toCSV, downloadFile } from '../../lib/importExport';
import AiDraftButton from '../AiDraftButton';
import { toast } from 'sonner';
import { invoiceOutstanding, invoiceDaysOverdue, invoiceDue, DEFAULT_TERMS_DAYS } from '../../lib/invoiceItems';

const bucket = (days) => typeof days !== 'number' ? 'unknown'
  : days > 90 ? '90+' : days > 60 ? '61-90' : days > 30 ? '31-60' : '1-30';
const bucketColor = (b) => ({ '1-30': 'bg-amber-100 text-amber-700', '31-60': 'bg-orange-100 text-orange-700', '61-90': 'bg-red-100 text-red-700', '90+': 'bg-red-200 text-red-800', unknown: 'bg-slate-100 text-slate-600' }[b]);

export default function CollectionsPage() {
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState('');
  const [terms, setTerms] = useState(DEFAULT_TERMS_DAYS);
  const [lateFee, setLateFee] = useState(20);
  const { contact } = useConfig();
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([apiInvoices.list(), apiCustomers.list()])
      .then(([inv, cust]) => { setInvoices(inv || []); setCustomers(cust || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
    // Terms decide the whole ageing, so they come from the company settings
    // rather than being assumed here.
    settingsApi.get()
      .then((cfg) => {
        setTerms(Number(cfg?.paymentTermsDays) || DEFAULT_TERMS_DAYS);
        if (cfg?.lateFeeAmount != null) setLateFee(Number(cfg.lateFeeAmount) || 0);
      })
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const custName = (inv) => inv.customerName || customers.find((c) => c.id === inv.customerId)?.name || 'Unknown';
  const balanceOf = (inv) => Math.max(0, invoiceOutstanding(inv));

  // Past-due = unpaid AND (past its due date OR already marked Overdue).
  // `days` was computed from `i.due`, which no invoice has, so it was 0 for
  // every row: the page showed "Oldest 0 days" against debt 443 days old and
  // bucketed all of it 1-30.
  const pastDue = invoices
    .filter((i) => String(i.status).toLowerCase() !== 'paid' && balanceOf(i) > 0)
    .map((i) => ({ ...i, days: invoiceDaysOverdue(i, terms), due: invoiceDue(i, terms), balance: balanceOf(i) }))
    .filter((i) => (i.days ?? 0) > 0 || String(i.status).toLowerCase() === 'overdue')
    .sort((a, b) => (b.days ?? -1) - (a.days ?? -1));

  const totalDue = pastDue.reduce((a, i) => a + i.balance, 0);
  const aged = pastDue.map((i) => i.days).filter((d) => typeof d === 'number');
  const oldest = aged.length ? Math.max(...aged) : 0;

  const letter = (inv) => printHTML(collectionLetterHTML(inv, custName(inv), inv.days ?? 0, fmt(inv.balance), contact));

  const sendReminder = async (inv) => {
    setSending(inv.id);
    const t = toast.loading(`Sending reminder for ${inv.id}…`);
    try { const r = await email.sendInvoice(inv.id); toast.success(`Reminder emailed to ${r.to}`, { id: t }); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Email not configured — add a Resend key in Settings to auto-send.', { id: t }); }
    finally { setSending(''); }
  };

  const exportList = () => {
    const rows = pastDue.map((i) => ({ inv: i.number || i.id, customer: custName(i), due: i.due, days: i.days, balance: i.balance }));
    downloadFile('xpertpests-collections.csv', toCSV(rows, [{ key: 'inv', header: 'Invoice' }, { key: 'customer', header: 'Customer' }, { key: 'due', header: 'Due' }, { key: 'days', header: 'Days Past Due' }, { key: 'balance', header: 'Balance' }]));
    toast.success('Exported collections list');
  };

  return (
    <div className="p-6 lg:p-8 space-y-5">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Collections</h1>
          <p className="text-slate-500 mt-1">Past-due accounts — send collection letters and reminders.</p>
        </div>
        <Button variant="outline" onClick={exportList} className="gap-1.5"><Download className="w-4 h-4" />Export</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="p-4 sm:p-5 border-slate-200 min-w-0 flex flex-wrap items-start gap-x-3">
          <div className="w-8 h-8 sm:w-11 sm:h-11 rounded-lg bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0 order-2"><DollarSign className="w-4 h-4 sm:w-5 sm:h-5" /></div>
          <div className="min-w-0 order-1 flex-1"><div className="text-[11px] sm:text-xs font-bold text-slate-500 uppercase tracking-wider leading-tight">Total Past Due</div></div>
          <div className="text-xl sm:text-2xl font-extrabold text-slate-900 tabular-nums mt-2 order-3 basis-full">{fmt(totalDue)}</div>
        </Card>
        <Card className="p-4 sm:p-5 border-slate-200 min-w-0 flex flex-wrap items-start gap-x-3">
          <div className="w-8 h-8 sm:w-11 sm:h-11 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center flex-shrink-0 order-2"><AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5" /></div>
          <div className="min-w-0 order-1 flex-1"><div className="text-[11px] sm:text-xs font-bold text-slate-500 uppercase tracking-wider leading-tight">Overdue Invoices</div></div>
          <div className="text-xl sm:text-2xl font-extrabold text-slate-900 tabular-nums mt-2 order-3 basis-full">{pastDue.length}</div>
        </Card>
        <Card className="p-4 sm:p-5 border-slate-200 min-w-0 flex flex-wrap items-start gap-x-3">
          <div className="w-8 h-8 sm:w-11 sm:h-11 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0 order-2"><Clock className="w-4 h-4 sm:w-5 sm:h-5" /></div>
          <div className="min-w-0 order-1 flex-1"><div className="text-[11px] sm:text-xs font-bold text-slate-500 uppercase tracking-wider leading-tight">Oldest</div></div>
          <div className="text-xl sm:text-2xl font-extrabold text-slate-900 tabular-nums mt-2 order-3 basis-full">{oldest} days</div>
        </Card>
      </div>

      {/* One row of actions per invoice, reachable on a phone.
          The table alone lays out 725px wide inside a 390px screen, so the
          Actions column - the AI draft, the letter and the reminder - sat off
          the right edge behind a horizontal scroll nobody finds. Below md the
          same rows render as cards; from md up the table is unchanged. */}
      <div className="md:hidden space-y-3">
        {loading && <div className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
        {!loading && pastDue.length === 0 && <Card className="p-8 text-center text-slate-500 border-slate-200">🎉 No past-due accounts. Everyone&apos;s paid up.</Card>}
        {pastDue.map((inv) => {
          const b = bucket(inv.days);
          return (
            <Card key={inv.id} className="p-4 border-slate-200 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <button onClick={() => inv.customerId && navigate(`/app/customers/${inv.customerId}`)}
                    className="font-semibold text-slate-900 text-left truncate block max-w-full py-1">{custName(inv)}</button>
                  <button onClick={() => navigate(`/app/accounting/invoices?open=${encodeURIComponent(inv.id)}`)}
                    className="text-xs font-medium text-[#71a80f] hover:underline py-1.5 -my-0.5 inline-block">#{inv.number || inv.id}</button>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-bold tabular-nums text-red-600">{fmt(inv.balance)}</div>
                  <div className="text-[11px] text-slate-500 whitespace-nowrap">due {inv.due || '—'}</div>
                </div>
              </div>
              <Badge className={`${bucketColor(b)} border-0 whitespace-nowrap`}>
                {typeof inv.days === 'number' ? `${inv.days} days · ${b}` : 'no date on file'}
              </Badge>
              <div className="flex gap-2 pt-1">
                <AiDraftButton kind="email" size="sm" variant="outline" label="AI draft"
                  customerName={custName(inv)}
                  context={`Friendly but firm past-due payment reminder. Invoice ${inv.number || inv.id}, balance ${fmt(inv.balance)}, ${inv.days ?? 'an unknown number of'} days past due. Remind them of our ${terms}-day payment terms and the ${fmt(lateFee)} late fee, and that they can pay online using the link/QR on their invoice. Keep it professional and warm.`} />
                <Button size="sm" variant="outline" className="flex-1 min-h-[40px] gap-1.5" onClick={() => letter(inv)}>
                  <FileText className="w-4 h-4 text-[#71a80f]" />Letter
                </Button>
                <Button size="sm" variant="outline" className="flex-1 min-h-[40px] gap-1.5"
                  onClick={() => sendReminder(inv)} disabled={sending === inv.id}>
                  {sending === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 text-slate-600" />}Remind
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="border-slate-200 overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3 text-left">Invoice</th><th className="px-5 py-3 text-left">Customer</th>
                <th className="px-5 py-3 text-left">Due</th><th className="px-5 py-3 text-left">Age</th>
                <th className="px-5 py-3 text-right">Balance</th><th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>}
              {!loading && pastDue.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-500">🎉 No past-due accounts. Everyone's paid up.</td></tr>}
              {pastDue.map((inv) => {
                const b = bucket(inv.days);
                return (
                  <tr key={inv.id} className="hover:bg-slate-50">
                    <td className="px-5 py-3"><button onClick={() => navigate(`/app/accounting/invoices?open=${encodeURIComponent(inv.id)}`)} className="font-semibold text-[#71a80f] hover:underline" title="Open invoice">{inv.number || inv.id}</button></td>
                    <td className="px-5 py-3"><button onClick={() => inv.customerId && navigate(`/app/customers/${inv.customerId}`)} className="font-medium text-slate-900 hover:text-[#71a80f] hover:underline text-left" title="Open customer">{custName(inv)}</button></td>
                    <td className="px-5 py-3 text-slate-600 whitespace-nowrap">{inv.due || '—'}</td>
                    <td className="px-5 py-3"><Badge className={`${bucketColor(b)} border-0 whitespace-nowrap`}>{typeof inv.days === 'number' ? `${inv.days}d · ${b}` : 'no date'}</Badge></td>
                    <td className="px-5 py-3 text-right font-bold tabular-nums text-red-600">{fmt(inv.balance)}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex gap-1 justify-end items-center">
                        <AiDraftButton kind="email" size="sm" variant="ghost" label="AI"
                          customerName={custName(inv)}
                          context={`Friendly but firm past-due payment reminder. Invoice ${inv.number || inv.id}, balance ${fmt(inv.balance)}, ${inv.days ?? 'an unknown number of'} days past due. Remind them of our ${terms}-day payment terms and the ${fmt(lateFee)} late fee, and that they can pay online using the link/QR on their invoice. Keep it professional and warm.`} />
                        <Button size="sm" variant="ghost" onClick={() => letter(inv)} title="Collection letter (PDF)"><FileText className="w-4 h-4 text-[#71a80f]" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => sendReminder(inv)} disabled={sending === inv.id} title="Email reminder">
                          {sending === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 text-slate-600" />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      <p className="text-xs text-slate-400">Tip: to auto-send collection letters on a schedule, add a Resend email key in Settings, then enable it under Automations.</p>
    </div>
  );
}
